import { QueryPageWarehouse } from '@dbt-labs/integrations/productHandoffs/productHandoffURIs';

import { DataPlatform } from '@/__generated__/graphql';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useLinkToDbtCloud } from '../links';
import {
  entityTypeToDataWarehouseObjectType,
  InsightsAssetType,
  InsightsResourceType,
  isAssetSupported,
  isResourceSupported,
  resourceTypeToDbtObjectType,
} from './types';

export const getWarehouseTypeFromPlatform = (
  platform: DataPlatform | null | undefined,
): QueryPageWarehouse => {
  switch (platform) {
    case DataPlatform.Snowflake:
      return 'snowflake';
    case DataPlatform.BigQuery:
      return 'bigquery';
    case DataPlatform.Redshift:
      return 'redshift';
    default:
      return 'snowflake';
  }
};

export const useInsightsDbtHandoff = ({
  resourceType,
  uniqueId,
  projectId,
  environmentId,
}: {
  resourceType: string;
  uniqueId: string;
  projectId?: number;
  environmentId?: number;
}) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId = 0 } = useDbtCloudAccount();
  const { projectId: inheritedProjectId, environmentId: inheritedEnvironmentId } =
    useDbtCloudProject();
  const validProjectId = projectId ?? inheritedProjectId ?? 0;
  const validEnvironmentId = environmentId ?? inheritedEnvironmentId ?? 0;
  const isEnabled = hasAccountFeature('dbtInsightsPage');

  const getInsightsDbtLink = useLinkToDbtCloud('insightsDbt');

  const showInsightsLink =
    isEnabled &&
    !!getInsightsDbtLink &&
    isResourceSupported(resourceType as InsightsResourceType) &&
    uniqueId !== '';

  const insightsHandoffLink = !showInsightsLink
    ? ''
    : getInsightsDbtLink({
        accountId,
        projectId: validProjectId,
        environmentId: validEnvironmentId,
        objectType: resourceTypeToDbtObjectType[resourceType as InsightsResourceType],
        objectId: uniqueId || '',
      });

  return {
    showInsightsLink,
    insightsHandoffLink,
    isEnabled,
    determiningInsightsAccess: false,
  };
};

export const useInsightsDWHHandoff = (
  assetType: string,
  uniqueId: string,
  platform: DataPlatform | null | undefined,
) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const isEnabled = hasAccountFeature('dbtInsightsPage');

  const getInsightsDWHLink = useLinkToDbtCloud('insightsDWH');

  const warehouseType = getWarehouseTypeFromPlatform(platform);

  const showInsightsLink =
    isEnabled &&
    !!getInsightsDWHLink &&
    isAssetSupported(assetType as InsightsAssetType);

  const insightsHandoffLink = !showInsightsLink
    ? ''
    : getInsightsDWHLink({
        accountId,
        projectId: projectId ?? 0,
        environmentId,
        objectType: entityTypeToDataWarehouseObjectType[assetType as InsightsAssetType],
        objectId: uniqueId || '',
        warehouseType,
      });

  return {
    showInsightsLink,
    insightsHandoffLink,
    isEnabled,
  };
};
